/* eslint-disable @typescript-eslint/no-unnecessary-boolean-literal-compare */
import { allDimensions } from '@upright/common/model/types';
import { RenderViewImpactChartParams } from '@upright/common/renderer/component-types/types-render-view-impact-chart';
import { RenderViewComponentType } from '@upright/common/renderer/types';
import { RenderViewButton } from '@upright/ui/common/generic/misc/render-view-button';
import { compact } from 'lodash';
import styled from 'styled-components';
import { useImpactChartContext, useImpactChartFontSizes, useImpactChartWidths } from '../context';
import { grayBorder } from '../shared';
import { BackLine } from './common/backline';
import { ModelReleaseText } from './common/model-version-text';
import { NetScores } from './common/net-scores';
import { SuggestFixes } from './common/suggest-fixes';
import { toRenderViewItems } from '../../../../../../../../common/renderer/get-render-view-url';
import { themeGet } from '@upright/ui/common/generic/theme/custom-theme';

export function BottomDesktop() {
  const {
    staticMode,
    onContributeClick,
    hideNetScore,
    version,
    release,
    language,
    isExpanded,
    data,
    showValueChains,
    scoreType,
    showRenderViewButton,
    entityData,
    hideReleaseText,
  } = useImpactChartContext();
  const widths = useImpactChartWidths();

  const showNetScore = !hideNetScore;
  const showSuggestFixes = !staticMode && !!onContributeClick;

  const minHeight = useMinHeight(showNetScore, showSuggestFixes);

  const hideDownloadButtonOnSmallScreens = showNetScore;

  const renderViewImpactChartParams: RenderViewImpactChartParams = {
    componentType: RenderViewComponentType.IMPACT_CHART,
    order: 0,
    isExpanded: compact(allDimensions.map(dimension => (isExpanded(dimension) ? dimension : undefined))),
    scoreType,
    showValueChains,
    hideNetScore: data.length > 1,
    showLegend: data.length > 1,
    hideTitle: data.length > 1,
  };

  return (
    <Root minHeight={minHeight} showBottomBorder={showNetScore}>
      {showNetScore && <BackLine />}
      {showNetScore && <NetScores />}
      <Main>
        <MainRight paddingLeft={widths.titleTextPadding}>
          {hideReleaseText !== true ? (
            <ModelReleaseText language={language} version={version} release={release} />
          ) : (
            <></>
          )}
          {showSuggestFixes && <SuggestFixes />}
        </MainRight>
        <MainLeft paddingRight={widths.titleTextPadding}>
          {showRenderViewButton && (
            <ButtonContainer hideButtonOnSmallScreens={hideDownloadButtonOnSmallScreens}>
              <RenderViewButton
                initialOptions={{
                  version,
                  release,
                  showControlUi: true,
                  items: toRenderViewItems(data),
                  componentParams: [renderViewImpactChartParams],
                }}
                data={entityData}
              />
            </ButtonContainer>
          )}
        </MainLeft>
      </Main>
    </Root>
  );
}

const Root = styled.div<{ minHeight?: number; showBottomBorder: boolean }>`
  min-height: ${props => props.minHeight}px;
  position: relative;
  border-bottom: ${props => props.showBottomBorder && `1px solid ${grayBorder}`};
`;

const Main = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
`;

const MainRight = styled.div<{ paddingLeft: number }>`
  padding-left: ${props => props.paddingLeft}px;
`;

const MainLeft = styled.div<{ paddingRight: number }>`
  padding-right: ${props => props.paddingRight}px;
`;

function useMinHeight(showNetScore: boolean | undefined, showSuggestFixes: boolean | undefined) {
  const { modelVersionText } = useImpactChartFontSizes();

  // Main is absolute-positioned inside Root, so Root's min-height has to fit the
  // tallest layout — otherwise Main's content (ModelReleaseText + download
  // button) overflows on top of the dimension blocks above it (most visibly the
  // environment block, which sits right above the Bottom). Earlier this short
  // circuited to 0 when net score and suggest-fixes were both hidden (e.g. in
  // the compare net-impact chart) which is what caused the overlap.
  const modelVersionPart = showNetScore ? 0 : modelVersionText * 5;
  const suggestFixesPart = showSuggestFixes ? modelVersionText * 4 : 0;

  const sum = modelVersionPart + suggestFixesPart;

  if (showNetScore && showSuggestFixes) {
    return sum + modelVersionText;
  }

  return sum;
}

const ButtonContainer = styled.div<{ hideButtonOnSmallScreens: boolean }>`
  @media (min-width: ${themeGet('breakpoints.3')}) {
    display: inline-block;
  }
  display: ${props => (props.hideButtonOnSmallScreens ? 'none;' : 'inline-block')};
`;
